// src/Leaderboard.js

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Avatar, Tag, Spin, Alert } from 'antd';
import { UserOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { db } from './Firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'; 

import './Leaderboard.css';

function Leaderboard({ userData }) {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasHistoryData, setHasHistoryData] = useState(false);
  const [leaderboardTitle, setLeaderboardTitle] = useState('Leaderboard');
  const [itemNumber, setItemNumber] = useState(null);
  const [itemScore, setItemScore] = useState(null);

  // [แก้ไข] 1. กลับมาใช้ฟังก์ชันเดียว โดยรับ isBackgroundUpdate
  const fetchLeaderboardData = useCallback(async (isBackgroundUpdate = false) => {
    if (!db) return;
    
    // (ถ้าไม่ใช่ background update (เช่น โหลดครั้งแรก) ให้โชว์ Loading)
    if (!isBackgroundUpdate) {
      setLoading(true);
    }
    
    // (เราจะ clear ค่าเก่าของ header ทุกครั้งที่ดึง 
    // เพื่อให้แน่ใจว่าค่าถูกต้องเสมอ)
    // setItemNumber(null);
    // setItemScore(null);
    
    let localItemNumber = null;
    let localItemScore = 0;
    let localCorrectAnswer = null;

    const correctAnswers = {};
    const questionScores = {};
    const levelMap = new Map();

    try {
      // --- 1. หาชื่อห้อง และ z-scale-x ---
      const roomConfigRef = doc(db, "Competition-Training", "nathadaj", "Admin", "Room");
      const roomConfigSnap = await getDoc(roomConfigRef);

      if (!roomConfigSnap.exists()) {
        throw new Error("ไม่พบเอกสารตั้งค่าห้อง (Admin/Room)");
      }
      
      const roomConfigData = roomConfigSnap.data(); 
      const roomName = roomConfigData.Meeting; 
      if (!roomName) {
        throw new Error("ไม่พบชื่อห้องในเอกสารตั้งค่า (field 'Meeting')");
      }

      for (let i = 1; i <= 20; i++) {
          const scaleField = `z-scale-${i}`;
          if (roomConfigData[scaleField] !== undefined) {
              questionScores[i] = roomConfigData[scaleField] || 0;
          }
      }

      // --- 2. ดึงข้อมูล Description, item, และ z-answer-x ---
      
      // (ดึง Title เฉพาะโหลดครั้งแรก)
      if (!isBackgroundUpdate) {
        try {
          const roomInfoRef = doc(db, "Competition-Training", "nathadaj", roomName, "Admin");
          const roomInfoSnap = await getDoc(roomInfoRef);
          if (roomInfoSnap.exists() && roomInfoSnap.data().description) {
            setLeaderboardTitle(roomInfoSnap.data().description);
          }
        } catch (descError) {
          console.warn(`ไม่สามารถดึงข้อมูล Title จาก .../${roomName}/Admin:`, descError.message);
        }
      }

      // (ดึง Item และ เฉลย ทุกครั้งที่อัปเดต)
      const roomInfoRef = doc(db, "Competition-Training", "nathadaj", roomName, "Admin");
      const roomInfoSnap = await getDoc(roomInfoRef);

      if (roomInfoSnap.exists()) {
        const adminData = roomInfoSnap.data();

        if (adminData.item !== undefined) {
          localItemNumber = adminData.item; 
          // [อัปเดต] ตั้งค่า Header ที่นี่
          setItemNumber(localItemNumber); 

          for (let i = 1; i <= 20; i++) {
              const ansField = `z-answer-${i}`;
              if (adminData[ansField] !== undefined) {
                  correctAnswers[i] = adminData[ansField];
              }
          }
          
          localItemScore = questionScores[localItemNumber] || 0;
          // [อัปเดต] ตั้งค่า Header ที่นี่
          setItemScore(localItemScore); 
          localCorrectAnswer = correctAnswers[localItemNumber]; 

        } else {
          console.warn(`ไม่พบฟิลด์ 'item' ในเอกสาร .../${roomName}/Admin`);
        }
      } else {
         console.warn(`ไม่พบเอกสาร .../${roomName}/Admin`);
      }
      

      // --- 3. ดึงข้อมูล Level (steps) ---
      const scoreCollectionRef = collection(db, "Competition-Training");
      const scoreSnapshot = await getDocs(scoreCollectionRef);
      scoreSnapshot.forEach((doc) => {
          const data = doc.data();
          const username = data.username;
          if (username) {
              const showSilver = data['show-silver'] || 0;
              const showGold = data['show-gold'] || 0;
              const showDiamond = data['show-diamond'] || 0;
              const totalSteps = showSilver + showGold + showDiamond;
              levelMap.set(username, totalSteps);
          }
      });


      // --- 4. ดึงข้อมูลผู้ใช้ทั้งหมด และ "คำนวณคะแนนรวม" ---
      const roomUsersCollectionRef = collection(db, "Competition-Training", "nathadaj", roomName);
      const roomUsersSnapshot = await getDocs(roomUsersCollectionRef);

      if (roomUsersSnapshot.empty) {
         throw new Error(`ไม่พบผู้ใช้ในห้อง ${roomName}`);
      }

      const fetchedUsers = [];
      roomUsersSnapshot.forEach((userDoc) => {
          const username = userDoc.id;
          const data = userDoc.data();
          const fullName = (data.name && data.surname) ? `${data.name} ${data.surname}` : data.name || data.surname || username;
          
          let calculatedTotalScore = 0;
          for (let x = 1; x <= 20; x++) {
              const correctAns = correctAnswers[x]; 
              const userAns = data[`answer-${x}`];
              const questScore = questionScores[x] || 0; 
              if (correctAns !== undefined && userAns !== undefined && String(userAns) === String(correctAns)) {
                  calculatedTotalScore += questScore;
              }
          }

          const userAnswerCurrent = data[`answer-${localItemNumber}`];
          const isCurrentCorrect = (localCorrectAnswer !== null && localCorrectAnswer !== undefined && userAnswerCurrent !== undefined && String(userAnswerCurrent) === String(localCorrectAnswer));
          const displayScore = isCurrentCorrect ? localItemScore : 0;
          const userLevel = levelMap.get(username) || 0;
          const hideUser = data.hide || false; 

          fetchedUsers.push({
              id: username, 
              name: fullName, 
              level: userLevel, 
              score: calculatedTotalScore, 
              answerResult: isCurrentCorrect, 
              answerScore: displayScore, 
              isCurrentUser: userData && userData.username === username,
              hide: hideUser, 
          });
      });


      if (fetchedUsers.length === 0) {
        setLeaderboardData([]);
      } else {
        const visibleUsers = fetchedUsers.filter(user => !user.hide);
        visibleUsers.sort((a, b) => {
          if (b.score !== a.score) { return b.score - a.score; }
          return a.level - b.level; 
        });
        const rankedData = visibleUsers.map((user, index) => ({
          ...user,
          ranking: index + 1,
        }));

        // [อัปเดต] ตั้งค่าตาราง ที่นี่
        setLeaderboardData(rankedData);
      }
      
      setError(null); 

    } catch (err) {
      if (err.code === 'permission-denied') {
        setError("Permission Denied: ตรวจสอบ Firestore Security Rules");
      } else {
        setError(`เกิดข้อผิดพลาด: ${err.message}`);
      }
      // (ถ้า error เราไม่เคลียร์ข้อมูลเก่า)
    } finally {
      // (ปิด Loading เฉพาะการโหลดครั้งแรก)
      if (!isBackgroundUpdate) {
        setLoading(false);
      }
    }
  }, [db, userData]);

  // [แก้ไข] 2. ปรับ useEffect ให้ใช้ setInterval กับฟังก์ชันตัวเต็ม
  useEffect(() => {
    if (db) {
      // 1. เรียกทันที 1 ครั้งสำหรับการโหลดหน้า (โชว์ Spin)
      fetchLeaderboardData(false);

      // 2. ตั้ง Interval ให้เรียกซ้ำทุก 5 วินาที (ไม่โชว์ Spin)
      const intervalId = setInterval(() => {
        fetchLeaderboardData(true); // (true = เป็น background update)
      }, 5000); // 5000ms = 5 วินาที

      // 3. Cleanup function:
      return () => {
        clearInterval(intervalId); // หยุดการเรียกซ้ำ
      };
    }
  }, [db, fetchLeaderboardData]); // (Dependency คือ fetchLeaderboardData)

  // (ส่วน checkHistoryData ไม่เปลี่ยนแปลง)
  const checkHistoryData = useCallback(async () => {
    if (!userData?.username || !db) {
      setHasHistoryData(false);
      return;
    }
    try {
      const historyCollectionRef = collection(db, "Competition-Training", userData.username, "History");
      const querySnapshot = await getDocs(historyCollectionRef);
      setHasHistoryData(!querySnapshot.empty);
    } catch (error) {
      console.error("Error checking history data:", error);
      setHasHistoryData(false);
    }
  }, [userData?.username, db]);

  useEffect(() => {
    if (userData?.username && db) {
      checkHistoryData();
    }
  }, [userData?.username, db, checkHistoryData]);


  // (ส่วน navigate ไม่เปลี่ยนแปลง)
  const handleViewQuestions = () => {
    navigate('/question'); 
  };
  const handleViewHistory = () => {
    navigate('/history');
  };

  // (ส่วน columns ไม่เปลี่ยนแปลง)
  const columns = [
    { title: 'Rank', dataIndex: 'ranking', key: 'ranking', align: 'center', 
      render: (text) => {
        if (text === 1) return (<div className="rank-cell gold">{text}</div>);
        if (text === 2) return (<div className="rank-cell silver">{text}</div>);
        if (text === 3) return (<div className="rank-cell bronze">{text}</div>);
        if (text >= 4 && text <= 10) return (<div className={`rank-cell rank-${text}`}>{text}</div>);
        return <div className="rank-cell">{text}</div>;
      },
    },
    { title: 'Name', dataIndex: 'name', key: 'name', 
      render: (text) => (<div className="name-cell"><Avatar icon={<UserOutlined />} className="avatar" />{text}</div>),
    },
    { title: 'คำตอบ', dataIndex: 'answerResult', key: 'answerResult', align: 'center',
      render: (isCorrect) => (isCorrect ? <CheckOutlined style={{ color: 'green', fontSize: '1.2em' }} /> : <CloseOutlined style={{ color: 'red', fontSize: '1.2em' }} />)
    },
    { title: 'คะแนน', dataIndex: 'answerScore', key: 'answerScore', align: 'center',
      render: (score) => (<span style={{ fontWeight: 'bold' }}>{score.toLocaleString()}</span>)
    },
    { title: 'คะแนนรวม', dataIndex: 'score', key: 'score', align: 'center', 
      render: (text) => (<span className="score-cell">{text.toLocaleString()} คะแนน</span>),
    },
  ];

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <div className="logo-container">
          <img 
            src="/Wisdom-Logo.png" 
            alt="Wisdom Logo" 
            className="logo-image1"
            // [แก้ไข] 3. คลิกโลโก้ = เรียก "โหลดเต็ม" (false)
            onClick={() => fetchLeaderboardData(false)} 
          />
        </div>
        
        <div className="title-container">
          <h1 className="leaderboard-title">
            {leaderboardTitle}
          </h1>
          {/* (ส่วนนี้จะอัปเดตทุก 5 วิ พร้อมตาราง) */}
          {itemNumber !== null && (
            <div className="leaderboard-subtitle">
              <span className="item-number">ข้อ : {itemNumber}</span>
              {itemScore !== null && (
                <span className="item-score">{itemScore.toLocaleString()} คะแนน</span>
              )}
            </div>
          )}
        </div>

        <div className="button-group">
          <button onClick={handleViewQuestions} className="back-button">
            คำตอบผู้แข่ง
          </button>
          {hasHistoryData && (
            <button onClick={handleViewHistory} className="history-button">
              History
            </button>
          )}
        </div>
      </div>

      {error && (
        <Alert
          message="ข้อผิดพลาด"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}

      {/* [แก้ไข] 4. ให้ Spin ทำงานเฉพาะเมื่อ loading (ครั้งแรก หรือกดโลโก้) */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>กำลังโหลด Leaderboard...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={leaderboardData}
          rowKey="id"
          pagination={false}
          bordered
          rowClassName={(record) => {
            let className = '';
            if (record.ranking >= 1 && record.ranking <= 10) {
              className += ` rank-${record.ranking}-row`;
            }
            if (record.isCurrentUser) className += ' current-user-row';
            return className.trim();
          }}
          locale={{ emptyText: 'ไม่มีข้อมูลใน Leaderboard' }}
          className="leaderboard-table"
        />
      )}
      
      {userData?.username && (
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.9em', color: '#666' }}>
          <p>Logged in as: {userData.username}</p>
        </div>
      )}
    </div>
  );
}

export default Leaderboard;